import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useRouter } from "next/router";
import { useApi } from "@/hooks/useApi";
import { useSectionTiming } from "@/hooks/useSectionTiming";
import { useQuery, useQueryClient } from "react-query";
import {
  fetchOrgs,
  GithubSsoReauthorizationRequiredException,
} from "@/hooks/useConnectedGithubOrgs";
import type { AxiosError, AxiosInstance } from "axios";
import SignedOut from "./SignedOut";
import { SidebarLayoutSkeleton } from "./SidebarLayout";
import { BlacksmithLogomarkAnimationSVG } from "./BlacksmithLogoSVG";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { BlackGitHubIcon, WhiteGitHubIcon } from "./GitHubIcon";
import { useTheme } from "next-themes";
import * as z from "zod";
import {
  emitGithubFirstSignupGtmEvent,
  readSignupConversionId,
  shouldEmitGithubFirstSignupGtm,
  stripGithubFirstSignupQuery,
} from "@/lib/githubAuthGtm";
import {
  clearOauthSuccessAppLoadGtmSessionFlag,
  emitOauthSuccessAppLoadGtmOnce,
} from "@/lib/oauthSuccessAppLoadGtm";
import { clearRdtCid } from "@/lib/appFirstTouchAttribution";
import { clearCodesmithLayoutCache } from "./sidebar/useCodesmithEnabledCache";

const userSchema = z.object({
  id: z.number(),
  is_superuser: z.boolean(),
  name: z.string(),
  username: z.string(),
  email: z.string(),
  avatar_url: z.string(),
  active_org_name: z.string().nullable().optional(),
  heard_from: z.string().nullable().optional(),
  first_name: z.string().nullable().optional(),
  session_count: z.number().int().nonnegative().nullable().optional(),
  // Whether this user should see in-progress/queued statuses in runs analytics.
  // Computed server-side as (rollout enabled || is_superuser). Optional to
  // tolerate older cached responses during deploys.
  show_live_statuses: z.boolean().optional(),
  codesmith_ack_preview_enabled: z.boolean().nullable().optional(),
});

type User = z.infer<typeof userSchema>;

interface UserContext {
  user: User | undefined;
  logout: () => void;
  setLoginModalOpen: (open: boolean) => void;
}

export const AuthContext = createContext<UserContext>({
  user: undefined,
  logout: () => {},
  // NB: Some other components can use this to effectively force the
  // user to refresh their session.
  setLoginModalOpen: () => {},
});

// Hook to access auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

async function fetchUser(api: AxiosInstance) {
  return api.get(`${process.env.NEXT_PUBLIC_API_URL}/api/user`);
}

// useLoginUser is a special hook meant to be used in the AuthProvider component.
// It intentionally does not use suspense, and instead returns a loading state and
// error. The AuthProvider component uses this hook to determine whether the user
// is logged in or not. If the user is not logged in, we show the SignedOut component.
export function useLoginUser() {
  const api = useApi();
  const { data, isLoading, error, isError, status } = useQuery<
    User,
    AxiosError
  >(
    "user",
    async () => {
      const response = await fetchUser(api);
      return response.data;
    },
    {
      suspense: false,
      // NB: We don't want to retry the query if it fails. If the user is not logged in, we will
      // show them the login modal. Re-running the query will cause the page/modal to flicker.
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      useErrorBoundary: false,
    },
  );

  return {
    user: data,
    isLoading,
    error,
    isError,
    status,
  };
}

export interface SessionExpirationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function SessionExpirationModal({
  open,
  setOpen,
}: SessionExpirationModalProps) {
  const { resolvedTheme } = useTheme();

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Your session has expired. Please log in again.
          </AlertDialogTitle>
          <AlertDialogDescription>
            You will be redirected to the login page.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            className="flex gap-2"
            variant="default"
            onClick={() => {
              const redirectUrl = encodeURIComponent(window.location.href);
              window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/login/github?redirect=${redirectUrl}`;
            }}
          >
            {resolvedTheme === "dark" ? (
              <BlackGitHubIcon />
            ) : (
              <WhiteGitHubIcon />
            )}
            Login
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Paths that handle their own authentication flow
const AUTH_BYPASS_PATHS = ["/oauth/authorize", "/cli/auth"];

// Pages that skip the login card for logged-out visitors and go straight to
// GitHub OAuth; the login redirect returns them to the same URL.
const AUTO_LOGIN_PATHS = ["/autofix-settings", "/[org]/autofix-settings"];

const AUTH_HAD_SESSION_KEY = "auth-had-session";

function getAuthHadSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(AUTH_HAD_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const api = useApi();
  const queryClient = useQueryClient();
  const firstSignupGtmHandledRef = useRef(false);
  const [firstAttemptFinished, setFirstAttemptFinished] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const logout = useCallback(() => {
    clearCodesmithLayoutCache();
    window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/logout`;
  }, []);
  const { user, isLoading, isError, error, status } = useLoginUser();

  useSectionTiming("auth", status);

  // Prefetch connected GitHub orgs in parallel with user fetch.
  // Both requests use cookie-based auth, so we don't need to wait for user data.
  // If unauthenticated, both will fail with 401 (which is fine - we show login screen).
  // This eliminates the waterfall: /api/user -> /user/github/orgs
  //
  // Use requestIdleCallback to defer the prefetch slightly so it doesn't compete
  // with critical first-paint resources. The Sidebar now uses non-suspense hooks,
  // so it can render immediately while this data loads in the background.
  useEffect(() => {
    const doPrefetch = () => {
      queryClient.prefetchQuery(
        "connectedGithubOrgs",
        () => fetchOrgs(api),
        { staleTime: Number.POSITIVE_INFINITY }, // Match useConnectedGithubOrgs config
      );
    };

    // Use requestIdleCallback when available for better scheduling.
    // Falls back to setTimeout for Safari.
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      (window as Window).requestIdleCallback(doPrefetch, { timeout: 200 });
    } else {
      // Safari fallback - small delay to let initial paint complete
      setTimeout(doPrefetch, 10);
    }
  }, [api, queryClient]);

  useEffect(() => {
    if (!router.isReady || firstSignupGtmHandledRef.current) {
      return;
    }
    if (!shouldEmitGithubFirstSignupGtm(router.query)) {
      return;
    }
    firstSignupGtmHandledRef.current = true;
    const conversionId = readSignupConversionId(router.query);
    emitGithubFirstSignupGtmEvent(conversionId);
    // Clear the stored rdt_cid now that it has been consumed by the signup.
    // This prevents it from being reused for a future signup on the same
    // browser (e.g. a shared computer or a second account creation).
    clearRdtCid();
    const nextQuery = stripGithubFirstSignupQuery(router.query);
    void router.replace(
      { pathname: router.pathname, query: nextQuery },
      undefined,
      { shallow: true },
    );
  }, [router]);

  const isUnauthorized = useMemo(() => {
    return isError && error?.response && error?.response.status === 401;
  }, [isError, error]);

  // Check if current path should bypass the default auth handling
  const shouldBypassAuth = useMemo(() => {
    return AUTH_BYPASS_PATHS.some((path) => router.pathname.startsWith(path));
  }, [router.pathname]);

  useEffect(() => {
    if (!router.isReady || !user || shouldBypassAuth) {
      return;
    }
    emitOauthSuccessAppLoadGtmOnce();
  }, [router.isReady, user, shouldBypassAuth]);

  // Session hint for loading UI: only after mount to avoid server/client hydration mismatch.
  const [useSkeletonForLoad, setUseSkeletonForLoad] = useState(false);
  useEffect(() => {
    setUseSkeletonForLoad(getAuthHadSession());
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (isUnauthorized && firstAttemptFinished) {
      setLoginModalOpen(true);
      clearOauthSuccessAppLoadGtmSessionFlag();
    } else if (!firstAttemptFinished && !isUnauthorized) {
      setFirstAttemptFinished(true);
      try {
        window.sessionStorage.setItem(AUTH_HAD_SESSION_KEY, "1");
      } catch {
        // ignore
      }
    }
  }, [
    isUnauthorized,
    firstAttemptFinished,
    setFirstAttemptFinished,
    isLoading,
  ]);

  // Clear session hint when showing sign-in page so next load shows minimal (no sidebar skeleton)
  useEffect(() => {
    if (isUnauthorized && !firstAttemptFinished && !shouldBypassAuth) {
      try {
        window.sessionStorage.removeItem(AUTH_HAD_SESSION_KEY);
        clearOauthSuccessAppLoadGtmSessionFlag();
      } catch {
        // ignore
      }
    }
  }, [isUnauthorized, firstAttemptFinished, shouldBypassAuth]);

  // Observe the connectedGithubOrgs query state without fetching it ourselves
  // (the deferred prefetch above owns fetching). The SAML SSO all-orgs-hidden
  // error is normally surfaced by suspense org hooks throwing to the error
  // boundary, but pages whose tree only mounts the non-suspense variants would
  // otherwise be left with an empty shell. Throwing here guarantees the
  // dedicated re-authorization screen renders on every authed page.
  const { error: orgsError } = useQuery(
    "connectedGithubOrgs",
    () => fetchOrgs(api),
    {
      enabled: false,
      staleTime: Number.POSITIVE_INFINITY,
    },
  );

  // If the query hit an error and its not an authorization error, we propagate it up
  // to the error boundary.
  if (isError && !isUnauthorized) {
    throw error;
  }

  if (
    !shouldBypassAuth &&
    orgsError instanceof GithubSsoReauthorizationRequiredException
  ) {
    throw orgsError;
  }

  // During initial auth check: sign-in path gets minimal (no sidebar); app path gets sidebar skeleton.
  if (isLoading && !firstAttemptFinished) {
    if (useSkeletonForLoad) {
      return <SidebarLayoutSkeleton />;
    }
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="w-32 h-32">
          <BlacksmithLogomarkAnimationSVG />
        </div>
      </div>
    );
  }

  // If the user is not logged in but they had the app open, this implies that
  // their session has expired. In that case, we will show them a login modal.
  // Otherwise, if the user is not logged in and they never had a successful login
  // attempt in this session, we show them the `SignedOut` component.
  // Exception: Pages that handle their own auth flow (like OAuth authorize) should
  // be allowed to render and handle auth themselves.
  if (isUnauthorized && !firstAttemptFinished && !shouldBypassAuth) {
    return (
      <>
        <SignedOut autoLogin={AUTO_LOGIN_PATHS.includes(router.pathname)} />
      </>
    );
  }
  return (
    <AuthContext.Provider value={{ user, logout, setLoginModalOpen }}>
      {children}
      <SessionExpirationModal
        open={loginModalOpen}
        setOpen={setLoginModalOpen}
      />
    </AuthContext.Provider>
  );
}
